import { Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { reorganizeGroups } from '../utils/merge';

const messages = defineMessages({
  merge: {
    id: 'autoCuration.pdfSplitEditor.mergeWithPage',
    defaultMessage: 'Merge with page:',
  },
  page: {
    id: 'autoCuration.pdfSplitEditor.pageNumber',
    defaultMessage: 'Page {num}',
  },
});

interface MergeMenuState {
  mouseX: number;
  mouseY: number;
  groupIndex: number;
}

interface MergeMenuProps {
  menuState: MergeMenuState | null;
  pageGroups: number[][];
  onMergePages: (newGroups: number[][]) => void;
  onClose: () => void;
}

export const MergeMenu = ({
  menuState,
  pageGroups,
  onMergePages,
  onClose,
}: MergeMenuProps) => {
  const { formatMessage } = useIntl();

  const handleMergePages = (targetGroupIndex: number, pageToMerge: number) => {
    const sourceGroupIndex = pageGroups.findIndex((group) =>
      group.includes(pageToMerge)
    );

    if (sourceGroupIndex === -1 || sourceGroupIndex === targetGroupIndex) {
      return pageGroups;
    }

    const newGroups = [...pageGroups];
    const sourceGroup = [...newGroups[sourceGroupIndex]];
    const targetGroup = [...newGroups[targetGroupIndex]];

    const pageIndex = sourceGroup.indexOf(pageToMerge);
    sourceGroup.splice(pageIndex, 1);

    targetGroup.push(pageToMerge);

    newGroups[targetGroupIndex] = targetGroup;

    if (sourceGroup.length === 0) {
      newGroups.splice(sourceGroupIndex, 1);
    } else {
      newGroups[sourceGroupIndex] = sourceGroup;
    }

    const result = reorganizeGroups(newGroups);
    onMergePages(result);
  };

  return (
    <Menu
      open={Boolean(menuState)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={
        menuState
          ? { top: menuState.mouseY, left: menuState.mouseX }
          : undefined
      }
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 300,
            width: 200,
          },
        },
      }}
    >
      <MenuItem disabled>
        <ListItemText primary={formatMessage(messages.merge)} />
      </MenuItem>
      <Divider />
      {menuState !== null &&
        pageGroups
          .flat()
          .sort((a, b) => a - b)
          .filter((page) => {
            // Filter out pages already in this group
            const currentGroup = pageGroups[menuState.groupIndex] || [];
            return !currentGroup.includes(page);
          })
          .map((page) => (
            <MenuItem
              key={page}
              onClick={() => handleMergePages(menuState.groupIndex, page)}
            >
              <ListItemText
                primary={formatMessage(messages.page, { num: page })}
              />
            </MenuItem>
          ))}
    </Menu>
  );
};

export type { MergeMenuProps, MergeMenuState };
